import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ==================== Phone Validation ====================

/**
 * Relaxed phone validation regex that allows common formats:
 * - +7 (995) 800-34-56
 * - +7 995 800 34 56
 * - +79958003456
 * - 8 (495) 123-45-67
 * - 8-800-555-35-35
 * - etc.
 */
export const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;

/**
 * More permissive phone regex for Russian and international formats
 * Allows: +7 (XXX) XXX-XX-XX, 8-XXX-XXX-XX-XX, etc.
 */
export const PHONE_REGEX_RELAXED = /^[\d\s()+.-]{7,20}$/;

/**
 * Validates a phone number with relaxed rules
 * @param phone - Phone number to validate
 * @returns true if valid
 */
export function isValidPhone(phone: string): boolean {
  if (!phone || phone.trim().length === 0) return false;
  // Strip all non-digit characters and check if we have at least 7 digits
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

/**
 * Sanitizes a phone number for API submission
 * Removes spaces, parentheses, dashes, and other formatting characters
 * @param phone - Phone number to sanitize
 * @returns Sanitized phone number (digits only, optionally with leading +)
 */
export function sanitizePhone(phone: string): string {
  if (!phone) return "";

  // Trim whitespace
  const trimmed = phone.trim();

  // Check if it starts with +
  const hasPlus = trimmed.startsWith("+");

  // Remove all non-digit characters
  const digits = trimmed.replace(/\D/g, "");

  // Return with + prefix if it had one
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Formats a phone number for display
 * @param phone - Phone number (digits only)
 * @returns Formatted phone number like +7 (999) 123-45-67
 */
export function formatPhone(phone: string): string {
  if (!phone) return "";

  const digits = phone.replace(/\D/g, "");

  // Russian format: 7/8 + 10 digits
  if (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))) {
    const prefix = digits.startsWith("7") ? "+7" : "8";
    return `${prefix} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  }

  // If 10 digits, assume Russian without country code
  if (digits.length === 10) {
    return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
  }

  // For other formats, just return as-is with + prefix
  if (digits.length > 0) {
    return `+${digits}`;
  }

  return phone;
}

// ==================== Error Translation ====================

/**
 * Translates backend error messages from English to Russian
 * @param error - Error message from backend
 * @returns Translated error message
 */
export function translateError(error: string | null | undefined): string {
  if (!error) return "Произошла ошибка";

  const errorLower = error.toLowerCase().trim();

  // Password reset and verification errors
  if (errorLower.includes("verification code expired") || errorLower.includes("code expired")) {
    return "Срок действия кода истек";
  }
  if (errorLower.includes("invalid verification code") || errorLower.includes("invalid code")) {
    return "Неверный код подтверждения";
  }
  if (errorLower.includes("verification code not found") || errorLower.includes("code not found") || errorLower.includes("reset code not found")) {
    return "Код не найден";
  }
  // Legacy support for old error messages
  if (errorLower.includes("invalid or expired code")) {
    return "Неверный или истекший код";
  }
  if (errorLower.includes("missing fields")) {
    return "Отсутствуют обязательные поля";
  }
  if (errorLower.includes("password too short")) {
    return "Пароль слишком короткий";
  }
  if (errorLower.includes("user not found")) {
    return "Пользователь не найден";
  }
  // Generic expired check (should come after specific checks)
  if (errorLower.includes("expired") && !errorLower.includes("code")) {
    return "Срок действия истек";
  }

  // Authentication errors
  if (errorLower.includes("invalid credentials")) {
    return "Неверные учетные данные";
  }
  if (errorLower.includes("unauthorized")) {
    return "Не авторизован";
  }
  if (errorLower.includes("forbidden")) {
    return "Доступ запрещен";
  }

  // Registration errors
  if (errorLower.includes("email already") || errorLower.includes("already exists") || errorLower.includes("already registered")) {
    return "Этот email уже зарегистрирован";
  }
  if (errorLower.includes("email is taken") || errorLower.includes("email taken")) {
    return "Этот email уже занят";
  }
  if (errorLower.includes("phone already") || errorLower.includes("phone is taken")) {
    return "Этот номер телефона уже зарегистрирован";
  }
  if (errorLower.includes("invalid email")) {
    return "Некорректный email";
  }
  if (errorLower.includes("invalid invite") || errorLower.includes("invite code invalid")) {
    return "Недействительный код приглашения";
  }
  if (errorLower.includes("invite code expired") || errorLower.includes("code expired")) {
    return "Код приглашения истёк";
  }
  if (errorLower.includes("invite code exhausted") || errorLower.includes("code exhausted")) {
    return "Код приглашения исчерпан";
  }
  if (errorLower.includes("weak password") || errorLower.includes("password too weak")) {
    return "Слишком слабый пароль";
  }
  if (errorLower.includes("account blocked") || errorLower.includes("account is blocked")) {
    return "Аккаунт заблокирован";
  }
  if (errorLower.includes("account pending") || errorLower.includes("pending approval")) {
    return "Аккаунт ожидает одобрения";
  }
  if (errorLower.includes("account rejected")) {
    return "Аккаунт отклонён";
  }
  if (errorLower.includes("duplicate") || errorLower.includes("unique constraint")) {
    return "Такая запись уже существует";
  }

  // Database errors - hide technical details
  if (errorLower.includes("sqlstate") || errorLower.includes("error:") || errorLower.includes("constraint") || errorLower.includes("relation")) {
    return "Произошла ошибка при обработке данных. Попробуйте позже.";
  }

  // Generic errors
  if (errorLower.includes("bad request")) {
    return "Неверный запрос";
  }
  if (errorLower.includes("internal server error")) {
    return "Внутренняя ошибка сервера";
  }
  if (errorLower.includes("network error") || errorLower.includes("network")) {
    return "Ошибка сети";
  }
  if (errorLower.includes("timeout")) {
    return "Превышено время ожидания";
  }

  // If no translation found, return original error
  return error;
}

// ==================== Preview URL ====================

/**
 * Gets the public URL for a site.
 * Priority:
 * 1. real custom domain
 * 2. generated preview_slug.wenlix.ru
 * 3. preview_url from API
 */
export function getPreviewUrl(site: {
  domain?: string;
  preview_slug?: string;
  preview_url?: string;
  ssl_enabled?: boolean;
}): string | undefined {
  if (site.domain) {
    return `https://${site.domain}`;
  }
  if (site.preview_slug) {
    const protocol = site.ssl_enabled ? "https" : "http";
    return `${protocol}://${site.preview_slug}.wenlix.ru`;
  }
  return site.preview_url;
}
